/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react'

import { Intent, Dialog, IDialogProps } from '@blueprintjs/core'
import cx from 'classnames'

import { Button, ButtonProps, Layout, Container, Text, ButtonVariation } from '../../'
import { Icon } from '@harnessio/icons'
import { Color, FontVariation } from '@harnessio/design-system'

import css from './ConfirmationDialog.css'
import { HarnessIconName } from '@harnessio/icons'

const getIconForIntent = (intent: Intent): HarnessIconName => {
  switch (intent) {
    case Intent.DANGER:
      return 'danger-icon'
    case Intent.WARNING:
      return 'warning-icon'
    case Intent.SUCCESS:
      return 'success-tick'
    case Intent.PRIMARY:
      return 'info-messaging'
    default:
      return 'info-messaging'
  }
}

export interface ConfirmationDialogProps extends Omit<IDialogProps, 'onClose' | 'enforceFocus'> {
  titleText: React.ReactNode
  contentText: React.ReactNode
  cancelButtonText?: React.ReactNode
  intent?: Intent
  buttonIntent?: ButtonProps['intent']
  buttonDisabled?: boolean
  confirmButtonText?: React.ReactNode
  onClose?: (isConfirmed: boolean) => void
  customButtons?: React.ReactNode
  showCloseButton?: boolean
  children?: JSX.Element
}

const confirmDialogProps: Partial<IDialogProps> = {
  usePortal: true,
  autoFocus: true,
  canEscapeKeyClose: true,
  canOutsideClickClose: true,
  enforceFocus: false,
  style: { width: 500, minHeight: 218 }
}

export function ConfirmationDialog(props: ConfirmationDialogProps): React.ReactElement {
  const {
    titleText,
    contentText,
    cancelButtonText,
    intent = Intent.NONE,
    buttonIntent = Intent.PRIMARY,
    buttonDisabled = false,
    confirmButtonText,
    onClose,
    customButtons,
    showCloseButton = true,
    children,
    className,
    ...rest
  } = props

  function closeWithFalse(): void {
    onClose?.(false)
  }

  function closeWithTrue(): void {
    onClose?.(true)
  }

  return (
    <Dialog className={cx(css.dialog, className)} {...confirmDialogProps} {...rest} onClose={closeWithFalse}>
      {showCloseButton ? (
        <Container flex className={css.iconContainer}>
          <Icon onClick={closeWithFalse} className={css.icon} size={8} name="main-close" />
        </Container>
      ) : null}

      <Layout.Horizontal className={css.header} padding={{ left: 'xsmall' }}>
        <Icon name={getIconForIntent(intent)} size={32} margin={{ right: 'small' }} intent={intent} />
        <Text font={{ variation: FontVariation.H4 }}>{titleText}</Text>
      </Layout.Horizontal>
      <Layout.Vertical
        font={{ variation: FontVariation.BODY }}
        color={Color.BLACK}
        margin={{ top: 'large', bottom: 'xxlarge' }}
        className={css.body}>
        {contentText}
      </Layout.Vertical>
      {children}
      <Layout.Horizontal spacing="small">
        {confirmButtonText ? (
          <Button intent={buttonIntent} disabled={buttonDisabled} text={confirmButtonText} onClick={closeWithTrue} />
        ) : null}
        {cancelButtonText ? (
          <Button variation={ButtonVariation.TERTIARY} text={cancelButtonText} onClick={closeWithFalse} />
        ) : null}
        {customButtons}
      </Layout.Horizontal>
    </Dialog>
  )
}
